/**
 * Hooks de TanStack Query.
 *
 * Las claves de caché siguen el patrón `[recurso, id, filtros]` para poder
 * invalidar por prefijo: tras una edición basta invalidar `['entidad', id]`
 * y se refrescan la tabla, el grafo y los contadores a la vez.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query'

import { api, comoQuery, filtrosAQuery } from './cliente'
import type {
  Corrida,
  Duplicado,
  Entidad,
  Enums,
  EntradaMaterialidad,
  FiltrosTabla,
  Fuente,
  Grafo,
  NuevaInvestigacion,
  Pagina,
  RegistroAuditoria,
  Relacion,
  Salud,
  Stakeholder,
  StakeholderFila,
  Tema,
} from './tipos'

export const claves = {
  salud: ['salud'] as const,
  enums: ['enums'] as const,
  entidades: ['entidades'] as const,
  entidad: (id: number) => ['entidad', id] as const,
  grafo: (id: number, incluirDescartados: boolean) =>
    ['entidad', id, 'grafo', incluirDescartados] as const,
  tabla: (id: number, filtros: FiltrosTabla) => ['entidad', id, 'tabla', filtros] as const,
  relaciones: (id: number) => ['entidad', id, 'relaciones'] as const,
  temas: (id: number) => ['entidad', id, 'temas'] as const,
  materialidad: (id: number) => ['entidad', id, 'materialidad'] as const,
  fuentes: (id: number, soloCitadas: boolean) =>
    ['entidad', id, 'fuentes', soloCitadas] as const,
  duplicados: (id: number) => ['entidad', id, 'duplicados'] as const,
  corridas: (id: number) => ['entidad', id, 'corridas'] as const,
  auditoria: (id: number) => ['entidad', id, 'auditoria'] as const,
  stakeholder: (id: number) => ['stakeholder', id] as const,
  corrida: (id: number) => ['corrida', id] as const,
}

// --------------------------------------------------------------------------
// Diagnóstico y vocabularios
// --------------------------------------------------------------------------

export function useSalud() {
  return useQuery({
    queryKey: claves.salud,
    queryFn: () => api.get<Salud>('/health/'),
    staleTime: 60_000,
  })
}

export function useEnums() {
  return useQuery({
    queryKey: claves.enums,
    queryFn: () => api.get<Enums>('/enums/'),
    // Los vocabularios son cerrados: no cambian mientras la app está abierta.
    staleTime: Infinity,
  })
}

// --------------------------------------------------------------------------
// Entidades
// --------------------------------------------------------------------------

/**
 * Trae también las descartadas, y la vista decide qué enseñar.
 *
 * Pedir solo las activas obligaba a una segunda petición para saber si hay
 * descartadas —y sin saberlo no se puede decidir si mostrar el interruptor
 * que las revela—. Son decenas de entidades, no miles: una petición y un
 * filtro en memoria sale más simple y más barato que dos.
 */
export function useEntidades() {
  return useQuery({
    queryKey: claves.entidades,
    queryFn: () => api.get<Pagina<Entidad>>('/entities/?incluir_descartadas=true'),
  })
}

export function useEntidad(id: number | undefined) {
  return useQuery({
    queryKey: claves.entidad(id!),
    queryFn: () => api.get<Entidad>(`/entities/${id}/`),
    enabled: id !== undefined,
  })
}

export function useGrafo(id: number | undefined, incluirDescartados = false) {
  return useQuery({
    queryKey: claves.grafo(id!, incluirDescartados),
    queryFn: () =>
      api.get<Grafo>(
        `/entities/${id}/graph/${comoQuery({ incluir_descartados: incluirDescartados })}`,
      ),
    enabled: id !== undefined,
  })
}

export function useTabla(id: number | undefined, filtros: FiltrosTabla) {
  return useQuery({
    queryKey: claves.tabla(id!, filtros),
    queryFn: () =>
      api.get<Pagina<StakeholderFila>>(
        `/entities/${id}/stakeholders/${filtrosAQuery(filtros)}`,
      ),
    enabled: id !== undefined,
    // Al cambiar de filtro se conserva la tabla anterior mientras llega la
    // nueva: evita el parpadeo a vacío en cada tecla del buscador.
    placeholderData: (previa) => previa,
  })
}

export function useRelaciones(id: number | undefined) {
  return useQuery({
    queryKey: claves.relaciones(id!),
    queryFn: () => api.get<Pagina<Relacion>>(`/entities/${id}/relations/`),
    enabled: id !== undefined,
  })
}

export function useTemas(id: number | undefined) {
  return useQuery({
    queryKey: claves.temas(id!),
    queryFn: () => api.get<Tema[]>(`/entities/${id}/topics/`),
    enabled: id !== undefined,
  })
}

export function useMaterialidad(id: number | undefined) {
  return useQuery({
    queryKey: claves.materialidad(id!),
    queryFn: () => api.get<EntradaMaterialidad[]>(`/entities/${id}/materiality/`),
    enabled: id !== undefined,
  })
}

export function useFuentes(id: number | undefined, soloCitadas = true) {
  return useQuery({
    queryKey: claves.fuentes(id!, soloCitadas),
    queryFn: () =>
      api.get<Pagina<Fuente>>(
        `/entities/${id}/sources/${comoQuery({ solo_citadas: soloCitadas })}`,
      ),
    enabled: id !== undefined,
  })
}

export function useDuplicados(id: number | undefined) {
  return useQuery({
    queryKey: claves.duplicados(id!),
    queryFn: () => api.get<Duplicado[]>(`/entities/${id}/duplicates/`),
    enabled: id !== undefined,
  })
}

export function useCorridas(id: number | undefined) {
  return useQuery({
    queryKey: claves.corridas(id!),
    queryFn: () => api.get<Pagina<Corrida>>(`/entities/${id}/runs/`),
    enabled: id !== undefined,
  })
}

export function useAuditoria(id: number | undefined) {
  return useQuery({
    queryKey: claves.auditoria(id!),
    queryFn: () => api.get<Pagina<RegistroAuditoria>>(`/entities/${id}/audit/`),
    enabled: id !== undefined,
  })
}

// --------------------------------------------------------------------------
// Stakeholder
// --------------------------------------------------------------------------

export function useStakeholder(
  id: number | undefined,
  opciones?: Partial<UseQueryOptions<Stakeholder>>,
) {
  return useQuery({
    queryKey: claves.stakeholder(id!),
    queryFn: () => api.get<Stakeholder>(`/stakeholders/${id}/`),
    enabled: id !== undefined,
    ...opciones,
  })
}

// --------------------------------------------------------------------------
// Corridas
// --------------------------------------------------------------------------

/**
 * Sondea una corrida hasta que termina.
 *
 * El §5 lo define así explícitamente: el backend no bloquea el request y el
 * frontend consulta el estado. El intervalo se detiene solo al terminar.
 */
export function useCorrida(id: number | undefined, activo = true) {
  return useQuery({
    queryKey: claves.corrida(id!),
    queryFn: () => api.get<Corrida>(`/runs/${id}/`),
    enabled: id !== undefined && activo,
    refetchInterval: (consulta) => {
      const corrida = consulta.state.data as Corrida | undefined
      return corrida?.terminada ? false : 2000
    },
  })
}

export function useNuevaInvestigacion() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (datos: NuevaInvestigacion) => api.post<Corrida>('/investigations/', datos),
    onSuccess: () => {
      cache.invalidateQueries({ queryKey: claves.entidades })
    },
  })
}

/** Oculta una investigación del listado. Reversible: no borra nada. */
export function useDescartarInvestigacion() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Entidad>(`/entities/${id}/discard/`),
    onSuccess: (_datos, id) => {
      cache.invalidateQueries({ queryKey: claves.entidades })
      cache.invalidateQueries({ queryKey: claves.entidad(id) })
    },
  })
}

export function useRestaurarInvestigacion() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Entidad>(`/entities/${id}/restore/`),
    onSuccess: (_datos, id) => {
      cache.invalidateQueries({ queryKey: claves.entidades })
      cache.invalidateQueries({ queryKey: claves.entidad(id) })
    },
  })
}

/** Borrado permanente: arrastra stakeholders, relaciones, fuentes y corridas. */
export function useBorrarInvestigacion() {
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/entities/${id}/?hard=true`),
    onSuccess: (_datos, id) => {
      cache.removeQueries({ queryKey: claves.entidad(id) })
      cache.invalidateQueries({ queryKey: claves.entidades })
    },
  })
}

// --------------------------------------------------------------------------
// Curaduría
// --------------------------------------------------------------------------

/** Invalida todo lo que cuelga de una entidad tras un cambio. */
function useInvalidarEntidad() {
  const cache = useQueryClient()
  return (entidadId: number) => {
    cache.invalidateQueries({ queryKey: ['entidad', entidadId] })
    cache.invalidateQueries({ queryKey: claves.entidades })
  }
}

export function useEditarStakeholder(entidadId: number) {
  const cache = useQueryClient()
  const invalidar = useInvalidarEntidad()

  return useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: Partial<Stakeholder> }) =>
      api.patch<Stakeholder>(`/stakeholders/${id}/`, cambios),

    // Actualización optimista con rollback si falla (§7).
    //
    // Se parchean dos sitios: la ficha del panel de detalle y todas las
    // páginas de tabla que ya estén en caché. Sin lo segundo, editar una
    // celda inline no se vería hasta que el servidor respondiera.
    onMutate: async ({ id, cambios }) => {
      await cache.cancelQueries({ queryKey: claves.stakeholder(id) })
      await cache.cancelQueries({ queryKey: ['entidad', entidadId, 'tabla'] })

      const previo = cache.getQueryData<Stakeholder>(claves.stakeholder(id))
      if (previo) {
        cache.setQueryData<Stakeholder>(claves.stakeholder(id), { ...previo, ...cambios })
      }

      const tablasPrevias = cache.getQueriesData<Pagina<StakeholderFila>>({
        queryKey: ['entidad', entidadId, 'tabla'],
      })
      for (const [clave, pagina] of tablasPrevias) {
        if (!pagina) continue
        cache.setQueryData<Pagina<StakeholderFila>>(clave, {
          ...pagina,
          results: pagina.results.map((fila) =>
            fila.id === id ? { ...fila, ...(cambios as Partial<StakeholderFila>) } : fila,
          ),
        })
      }

      return { previo, tablasPrevias }
    },
    onError: (_error, { id }, contexto) => {
      if (contexto?.previo) cache.setQueryData(claves.stakeholder(id), contexto.previo)
      for (const [clave, pagina] of contexto?.tablasPrevias ?? []) {
        cache.setQueryData(clave, pagina)
      }
    },
    onSettled: (_datos, _error, { id }) => {
      cache.invalidateQueries({ queryKey: claves.stakeholder(id) })
      invalidar(entidadId)
    },
  })
}

export function useDescartarStakeholder(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  const cache = useQueryClient()
  return useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo?: string }) =>
      api.post<Stakeholder>(`/stakeholders/${id}/discard/`, { motivo }),
    onSuccess: (_datos, { id }) => {
      cache.invalidateQueries({ queryKey: claves.stakeholder(id) })
      invalidar(entidadId)
    },
  })
}

export function useRestaurarStakeholder(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => api.post<Stakeholder>(`/stakeholders/${id}/restore/`),
    onSuccess: (_datos, id) => {
      cache.invalidateQueries({ queryKey: claves.stakeholder(id) })
      invalidar(entidadId)
    },
  })
}

export function useBorrarStakeholder(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/stakeholders/${id}/?hard=true`),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useFusionar(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: ({ destinoId, origenIds }: { destinoId: number; origenIds: number[] }) =>
      api.post<Stakeholder>('/stakeholders/merge/', {
        destino_id: destinoId,
        origen_ids: origenIds,
      }),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useDecidirDuplicado(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: ({
      id,
      decision,
      destinoId,
    }: {
      id: number
      decision: 'fusionar' | 'distintos'
      destinoId?: number
    }) =>
      api.post<Duplicado>(`/duplicates/${id}/decide/`, {
        decision,
        ...(destinoId ? { destino_id: destinoId } : {}),
      }),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useProfundizarStakeholder(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: ({ id, forzar }: { id: number; forzar?: boolean }) =>
      api.post<Corrida>(`/stakeholders/${id}/deepen/`, { forzar }),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useProfundizarEntidad(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: (datos: { categoria?: string; tema?: string; forzar?: boolean }) =>
      api.post<Corrida>(`/entities/${entidadId}/deepen/`, datos),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useVerificarFuentes(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  const cache = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      api.post<{ encontradas: number; nuevas: number; stakeholder: Stakeholder }>(
        `/stakeholders/${id}/verify/`,
      ),
    onSuccess: (_datos, id) => {
      cache.invalidateQueries({ queryKey: claves.stakeholder(id) })
      invalidar(entidadId)
    },
  })
}

export function useEditarRelacion(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: ({ id, cambios }: { id: number; cambios: Partial<Relacion> }) =>
      api.patch<Relacion>(`/relations/${id}/`, cambios),
    onSuccess: () => invalidar(entidadId),
  })
}

export function useDescartarRelacion(entidadId: number) {
  const invalidar = useInvalidarEntidad()
  return useMutation({
    mutationFn: (id: number) => api.post<Relacion>(`/relations/${id}/discard/`),
    onSuccess: () => invalidar(entidadId),
  })
}
